import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BADGE_CATALOG } from "@/components/Badges";
import { Sparkles, X } from "lucide-react";

const CONGRATS_LINES = [
    "أحسنتِ! خطوة جديدة نحو التميّز 🌟",
    "استمري بهذا التألّق، فأنتِ في الطريق الصحيح!",
    "مجهودك أثمر — لكِ منّا كل الفخر ✨",
    "شارة جديدة تُضاف إلى إنجازاتك، للأمام دائماً!",
];

function Confetti() {
    // Lightweight CSS confetti — 40 shards
    const shards = useMemo(() => {
        const colors = ["#F59E0B", "#C8102E", "#1B2A4E", "#7C3AED", "#10B981", "#EC4899", "#0EA5E9"];
        return Array.from({ length: 40 }).map((_, i) => ({
            id: i,
            left: Math.random() * 100,
            delay: Math.random() * 0.6,
            duration: 1.6 + Math.random() * 1.4,
            color: colors[i % colors.length],
            rot: Math.random() * 360,
            size: 6 + Math.random() * 6,
        }));
    }, []);
    return (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
            {shards.map((s) => (
                <span
                    key={s.id}
                    className="absolute top-[-10%] rounded-sm confetti-shard"
                    style={{
                        left: `${s.left}%`, width: s.size, height: s.size * 0.4,
                        backgroundColor: s.color,
                        transform: `rotate(${s.rot}deg)`,
                        animation: `bqns-confetti-fall ${s.duration}s ${s.delay}s linear forwards`,
                    }}
                />
            ))}
            <style>{`
                @keyframes bqns-confetti-fall {
                    0% { transform: translateY(0) rotate(0deg); opacity: 1; }
                    100% { transform: translateY(560px) rotate(720deg); opacity: 0; }
                }
                @keyframes bqns-badge-pop {
                    0% { transform: scale(0.4); opacity: 0; }
                    60% { transform: scale(1.15); opacity: 1; }
                    100% { transform: scale(1); opacity: 1; }
                }
                @keyframes bqns-ring-pulse {
                    0%, 100% { box-shadow: 0 0 0 0 var(--bqns-ring); }
                    50% { box-shadow: 0 0 0 18px transparent; }
                }
            `}</style>
        </div>
    );
}

/**
 * Queue-based celebration modal.
 * Props:
 *  - badgeIds: array of newly earned badge ids
 *  - onDismiss: called with the array of shown badge ids once user closes/completes
 */
export default function BadgeCelebration({ badgeIds = [], onDismiss }) {
    const [idx, setIdx] = useState(0);
    const [open, setOpen] = useState(badgeIds.length > 0);

    useEffect(() => {
        if (badgeIds.length > 0) { setIdx(0); setOpen(true); }
    }, [badgeIds.join(",")]);

    if (!open || badgeIds.length === 0) return null;
    const current = badgeIds[idx];
    const b = BADGE_CATALOG[current];
    if (!b) return null;
    const Icon = b.icon;
    const line = CONGRATS_LINES[idx % CONGRATS_LINES.length];
    const isLast = idx >= badgeIds.length - 1;

    const finish = () => {
        setOpen(false);
        onDismiss && onDismiss(badgeIds);
    };
    const next = () => (isLast ? finish() : setIdx((i) => i + 1));

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) finish(); }}>
            <DialogContent
                dir="rtl"
                data-testid="badge-celebration"
                className="max-w-md p-0 overflow-hidden border-0 bg-transparent shadow-none"
            >
                <div
                    className="relative rounded-2xl overflow-hidden text-center"
                    style={{ background: `linear-gradient(160deg, ${b.color} 0%, #0F172A 100%)` }}
                >
                    <Confetti />
                    <button
                        onClick={finish}
                        data-testid="badge-celebration-close"
                        className="absolute top-3 left-3 z-10 p-1.5 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                        aria-label="إغلاق"
                    >
                        <X className="w-4 h-4" />
                    </button>
                    <div className="relative px-8 pt-10 pb-8 text-white">
                        <div className="flex items-center justify-center gap-2 text-xs uppercase tracking-widest text-white/70 mb-2">
                            <Sparkles className="w-4 h-4" />
                            شارة جديدة!
                            <Sparkles className="w-4 h-4" />
                        </div>
                        <div className="text-white/90 text-sm mb-6">لقد كسبتِ</div>
                        <div
                            className="w-28 h-28 mx-auto rounded-full flex items-center justify-center text-white ring-4 ring-white/30"
                            style={{
                                backgroundColor: "#ffffff20",
                                animation: "bqns-badge-pop 0.7s ease-out, bqns-ring-pulse 2s ease-in-out 0.7s infinite",
                                ["--bqns-ring"]: `${b.color}90`,
                            }}
                        >
                            <Icon width={56} height={56} />
                        </div>
                        <h2 className="text-3xl font-bold mt-6" data-testid={`badge-celebration-name-${current}`}>{b.name}</h2>
                        <p className="text-white/80 mt-2 text-sm">{b.hint}</p>
                        <p className="text-white/90 mt-6 text-base font-medium">{line}</p>
                        {badgeIds.length > 1 && (
                            <div className="text-xs text-white/60 mt-4 font-mono-num" dir="ltr">
                                {idx + 1} / {badgeIds.length}
                            </div>
                        )}
                        <Button
                            onClick={next}
                            data-testid="badge-celebration-next"
                            className="mt-6 w-full h-11 bg-white text-slate-900 hover:bg-white/90 font-bold"
                        >
                            {isLast ? "شكراً، هيا نُواصل!" : "التالي"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
